import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Markdown from 'react-markdown';
import {
  LifeBuoy, ChevronRight, CheckCircle2, Circle, Loader2,
  Upload, AlertTriangle, Phone, FileText, Clock,
  MessageSquarePlus, RefreshCw, ChevronDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-300', bg: 'border-l-red-500' },
  serious: { label: 'Serious', color: 'bg-orange-100 text-orange-700 border-orange-300', bg: 'border-l-orange-500' },
  moderate: { label: 'Moderate', color: 'bg-amber-100 text-amber-700 border-amber-300', bg: 'border-l-amber-500' },
  manageable: { label: 'Manageable', color: 'bg-green-100 text-green-700 border-green-300', bg: 'border-l-green-500' },
};

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-green-100 text-green-700',
};

export default function RescuePlanPage() {
  const { api } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('loading'); // loading | questionnaire | upload | generating | plan
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState([]);
  const [uploadNotes, setUploadNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [costs, setCosts] = useState(null);
  const [expandedStep, setExpandedStep] = useState(null);
  const [stepUpdates, setStepUpdates] = useState({});
  const [newUpdate, setNewUpdate] = useState('');
  const [reviews, setReviews] = useState([]);
  const [reviewing, setReviewing] = useState(false);
  const [showReviews, setShowReviews] = useState(false);

  const fetchPlan = useCallback(async () => {
    try {
      const res = await api.get('/rescue-plan');
      if (res.data && res.data.id) {
        setPlan(res.data);
        setPhase('plan');
      } else {
        setPhase('questionnaire');
      }
    } catch { setPhase('questionnaire'); }
    finally { setLoading(false); }
  }, [api]);

  const fetchQuestionnaire = useCallback(async () => {
    try {
      const res = await api.get('/rescue-plan/questionnaire');
      setQuestionnaire(res.data.sections);
    } catch {}
  }, [api]);

  const fetchCosts = useCallback(async () => {
    try {
      const res = await api.get('/rescue-plan/costs');
      setCosts(res.data);
    } catch {}
  }, [api]);

  useEffect(() => { fetchPlan(); fetchQuestionnaire(); fetchCosts(); fetchReviews(); }, [fetchPlan, fetchQuestionnaire, fetchCosts]);

  const submitQuestionnaire = async () => {
    try {
      await api.post('/rescue-plan/start', { answers });
      toast.success('Questionnaire saved');
      setPhase('upload');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadDocuments = async () => {
    if (files.length === 0) {
      setPhase('generating');
      generatePlan();
      return;
    }
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('notes', uploadNotes);
    try {
      await api.post('/rescue-plan/upload-documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${files.length} documents uploaded`);
      setPhase('generating');
      generatePlan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/rescue-plan/generate');
      toast.success(`Recovery plan generated (${res.data.steps_generated} steps, ${res.data.credits_used} credits)`);
      fetchPlan();
      fetchCosts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate'); setPhase('upload'); }
    finally { setGenerating(false); }
  };

  const toggleStep = async (stepId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await api.put(`/rescue-plan/steps/${stepId}`, { status: newStatus });
      fetchPlan();
    } catch { toast.error('Failed'); }
  };

  const loadStepUpdates = async (stepId) => {
    if (expandedStep === stepId) { setExpandedStep(null); return; }
    try {
      const res = await api.get(`/rescue-plan/steps/${stepId}/updates`);
      setStepUpdates(prev => ({ ...prev, [stepId]: res.data }));
      setExpandedStep(stepId);
      setNewUpdate('');
    } catch {}
  };

  const submitStepUpdate = async (stepId) => {
    if (!newUpdate.trim()) return;
    try {
      await api.post(`/rescue-plan/steps/${stepId}/updates`, { text: newUpdate });
      toast.success('Update added');
      setNewUpdate('');
      loadStepUpdates(stepId);
      fetchPlan();
    } catch { toast.error('Failed'); }
  };

  const runWeeklyReview = async () => {
    setReviewing(true);
    try {
      const res = await api.post('/rescue-plan/weekly-review');
      toast.success('Weekly review complete');
      setReviews(prev => [res.data, ...prev]);
      setShowReviews(true);
      fetchCosts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setReviewing(false); }
  };

  const fetchReviews = async () => {
    try {
      const res = await api.get('/rescue-plan/reviews');
      setReviews(res.data);
    } catch {}
  };

  // ─── Loading ───
  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  // ─── Generating ───
  if (phase === 'generating' || generating) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-6" data-testid="generating">
        <Loader2 className="w-16 h-16 animate-spin text-primary" />
        <div className="text-center">
          <h2 className="text-xl font-bold">Building Your Recovery Plan</h2>
          <p className="text-muted-foreground mt-1">Analysing your situation and creating a step-by-step guide...</p>
          <p className="text-xs text-muted-foreground mt-2">This may take 30-60 seconds</p>
        </div>
      </div>
    );
  }

  // ─── Questionnaire Phase ───
  if (phase === 'questionnaire' && questionnaire) {
    return (
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="questionnaire-phase">
        <div className="text-center">
          <LifeBuoy className="w-12 h-12 mx-auto text-primary mb-3" />
          <h1 className="text-2xl font-bold">Rescue Plan</h1>
          <p className="text-muted-foreground">Let's understand your situation so we can build a recovery plan</p>
        </div>

        {questionnaire.map((section) => (
          <Card key={section.id}>
            <CardHeader>
              <CardTitle className="text-base">{section.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {section.questions.map((q) => {
                // Skip dependent questions if parent is "no"
                if (q.depends_on && !answers[q.depends_on]) return null;
                if (q.depends_on && answers[q.depends_on] === false) return null;

                return (
                  <div key={q.id} className={q.depends_on ? 'ml-4 pl-3 border-l-2 border-muted' : ''}>
                    <Label className="text-sm">{q.text}</Label>
                    {q.type === 'yesno' && (
                      <div className="flex gap-2 mt-1">
                        <Button size="sm" variant={answers[q.id] === true ? 'default' : 'outline'} onClick={() => setAnswers(a => ({ ...a, [q.id]: true }))}>Yes</Button>
                        <Button size="sm" variant={answers[q.id] === false ? 'default' : 'outline'} onClick={() => setAnswers(a => ({ ...a, [q.id]: false }))}>No</Button>
                      </div>
                    )}
                    {q.type === 'text' && (
                      <Textarea className="mt-1" rows={2} value={answers[q.id] || ''} onChange={(e) => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} data-testid={`q-${q.id}`} />
                    )}
                    {q.type === 'currency' && (
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-2 text-muted-foreground">£</span>
                        <Input className="pl-7" type="number" value={answers[q.id] || ''} onChange={(e) => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} data-testid={`q-${q.id}`} />
                      </div>
                    )}
                    {q.type === 'number' && (
                      <Input className="mt-1 w-24" type="number" value={answers[q.id] || ''} onChange={(e) => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} data-testid={`q-${q.id}`} />
                    )}
                    {q.type === 'choice' && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {q.options.map(opt => (
                          <Button key={opt} size="sm" variant={answers[q.id] === opt ? 'default' : 'outline'} onClick={() => setAnswers(a => ({ ...a, [q.id]: opt }))}>{opt}</Button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}

        <Button className="w-full" size="lg" onClick={submitQuestionnaire} data-testid="submit-questionnaire-btn">
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  // ─── Upload Phase ───
  if (phase === 'upload') {
    return (
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="upload-phase">
        <div className="text-center">
          <Upload className="w-12 h-12 mx-auto text-primary mb-3" />
          <h1 className="text-2xl font-bold">Upload Documents</h1>
          <p className="text-muted-foreground">Upload any documents that show your financial situation</p>
        </div>

        <Card>
          <CardContent className="py-6">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                type="file"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files))}
                className="hidden"
                id="rescue-file-input"
                accept=".pdf,.csv,.jpg,.jpeg,.png,.xlsx,.xls"
                data-testid="file-input"
              />
              <label htmlFor="rescue-file-input" className="cursor-pointer">
                <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                <p className="font-medium">Click to upload documents</p>
                <p className="text-xs text-muted-foreground mt-1">Bank statements, invoices, utility bills, HMRC letters, loan docs, lease agreement — anything relevant</p>
              </label>
            </div>
            {files.length > 0 && (
              <div className="mt-4 space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span>{f.name}</span>
                    <span className="text-xs text-muted-foreground">({(f.size / 1024).toFixed(0)} KB)</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-4">
            <Label>Additional notes or context</Label>
            <Textarea
              className="mt-1"
              rows={3}
              placeholder="Anything else we should know — verbal agreements, promises made, threats received, etc."
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              data-testid="upload-notes"
            />
          </CardContent>
        </Card>

        <Button className="w-full" size="lg" onClick={uploadDocuments} data-testid="generate-plan-btn">
          {files.length > 0 ? `Upload ${files.length} files & Generate Plan` : 'Skip uploads & Generate Plan'}
        </Button>
      </div>
    );
  }

  // ─── Plan View ───
  if (phase === 'plan' && plan) {
    const sevCfg = SEVERITY_CONFIG[plan.severity] || SEVERITY_CONFIG.serious;
    const weekGroups = {};
    (plan.steps || []).forEach(s => {
      const key = `Week ${s.week}: ${s.week_title}`;
      if (!weekGroups[key]) weekGroups[key] = [];
      weekGroups[key].push(s);
    });

    return (
      <div className="space-y-6" data-testid="plan-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Rescue Plan</h1>
            <p className="text-muted-foreground text-sm">{plan.summary}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={sevCfg.color} data-testid="severity-badge">{sevCfg.label}</Badge>
            {costs && (
              <Badge variant="outline" className="text-xs" data-testid="cost-badge">
                Service value: £{costs.total_value_gbp.toFixed(2)}
              </Badge>
            )}
            <Button onClick={runWeeklyReview} disabled={reviewing} size="sm" data-testid="weekly-review-btn">
              {reviewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Weekly Review
            </Button>
          </div>
        </div>

        {/* Progress */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Recovery Progress</span>
              <span className="text-sm text-muted-foreground">{plan.completed_steps}/{plan.total_steps} steps</span>
            </div>
            <Progress value={plan.progress_pct} className="h-3" />
          </CardContent>
        </Card>

        {/* Immediate Actions */}
        {plan.immediate_actions && plan.immediate_actions.length > 0 && (
          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" /> Do Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1">
                {plan.immediate_actions.map((a, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="text-red-600 font-bold">{i + 1}.</span> {a}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Financial Snapshot */}
        {plan.financial_snapshot && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="py-3 text-center">
              <p className="text-lg font-bold text-red-600">£{(plan.financial_snapshot.estimated_total_debt || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Est. Total Debt</p>
            </CardContent></Card>
            <Card><CardContent className="py-3 text-center">
              <p className="text-lg font-bold">£{(plan.financial_snapshot.estimated_monthly_burn || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Monthly Burn</p>
            </CardContent></Card>
            <Card><CardContent className="py-3 text-center">
              <p className="text-lg font-bold text-green-600">£{(plan.financial_snapshot.estimated_monthly_revenue || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Monthly Revenue</p>
            </CardContent></Card>
            <Card><CardContent className="py-3 text-center">
              <p className="text-lg font-bold">{plan.financial_snapshot.cash_runway_weeks || '?'}</p>
              <p className="text-xs text-muted-foreground">Weeks Cash Runway</p>
            </CardContent></Card>
          </div>
        )}

        <Separator />

        {/* Weekly Steps */}
        {Object.entries(weekGroups).map(([weekTitle, steps]) => (
          <div key={weekTitle} className="space-y-2">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{weekTitle}</h3>
            {steps.map((step) => {
              const done = step.status === 'completed';
              const priColor = PRIORITY_COLORS[step.priority] || PRIORITY_COLORS.medium;
              return (
                <Card key={step.id} className={`${done ? 'opacity-60' : ''}`} data-testid={`step-${step.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleStep(step.id, step.status)} className="mt-0.5 shrink-0" data-testid={`toggle-step-${step.id}`}>
                        {done ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 text-muted-foreground hover:text-primary" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-medium ${done ? 'line-through' : ''}`}>{step.title}</span>
                          <Badge variant="outline" className={`text-[10px] h-4 ${priColor}`}>{step.priority}</Badge>
                          {step.deadline_days && !done && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <Clock className="w-3 h-3" /> {step.deadline_days}d
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                        {step.contact && (
                          <p className="text-xs mt-1 flex items-center gap-1 text-primary">
                            <Phone className="w-3 h-3" /> {step.contact}
                          </p>
                        )}
                        {step.script && (
                          <div className="mt-2 p-2 bg-muted/50 rounded text-xs italic">"{step.script}"</div>
                        )}
                        {step.notes && expandedStep !== step.id && (
                          <p className="text-xs mt-1 text-muted-foreground italic">Latest: {step.notes}</p>
                        )}
                      </div>
                      <button
                        onClick={() => loadStepUpdates(step.id)}
                        className="shrink-0 text-muted-foreground hover:text-primary"
                        data-testid={`expand-step-${step.id}`}
                      >
                        {expandedStep === step.id ? <ChevronDown className="w-4 h-4" /> : <MessageSquarePlus className="w-4 h-4" />}
                      </button>
                    </div>

                    {/* Expanded: Updates timeline + add update */}
                    {expandedStep === step.id && (
                      <div className="mt-3 ml-8 border-l-2 border-muted pl-3 space-y-2">
                        {/* Add update form */}
                        <div className="flex gap-2">
                          <input
                            className="flex-1 text-sm border rounded px-2 py-1 bg-background"
                            placeholder="Add an update... e.g. 'Called HMRC, agreed 12-month plan at £200/month'"
                            value={newUpdate}
                            onChange={(e) => setNewUpdate(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && submitStepUpdate(step.id)}
                            data-testid={`update-input-${step.id}`}
                          />
                          <Button size="sm" variant="outline" onClick={() => submitStepUpdate(step.id)} data-testid={`submit-update-${step.id}`}>
                            Add
                          </Button>
                        </div>
                        {/* Timeline */}
                        {(stepUpdates[step.id] || []).map((u) => (
                          <div key={u.id} className="text-xs">
                            <span className="text-muted-foreground">{new Date(u.created_at).toLocaleDateString('en-GB')} — {u.user_name}:</span>
                            <span className="ml-1">{u.text}</span>
                          </div>
                        ))}
                        {(stepUpdates[step.id] || []).length === 0 && (
                          <p className="text-xs text-muted-foreground">No updates yet</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}

        {/* Resources */}
        {plan.resources && plan.resources.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Useful Resources</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {plan.resources.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium">{r.name}</span>
                    {r.notes && <span className="text-muted-foreground ml-2">— {r.notes}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {r.phone && <span><Phone className="w-3 h-3 inline" /> {r.phone}</span>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Cost Acknowledgment */}
        {costs && costs.total_value_gbp > 0 && (
          <Card className="bg-muted/30">
            <CardContent className="py-3 text-center text-sm text-muted-foreground">
              Total advisory service value: <span className="font-semibold text-foreground">£{costs.total_value_gbp.toFixed(2)}</span>
              <span className="mx-2">|</span>
              {costs.total_credits} AI credits used
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return null;
}
